import React, { useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Button from 'react-bootstrap-button-loader';
import FormControl from '@material-ui/core/FormControl';
import TextField from "@material-ui/core/TextField";
import { savePIN } from '../../actions/security';
import * as StatusCode from '../../scripts/PINStatusCode';
import i18next from 'i18next';
import { withNamespaces } from 'react-i18next';
import { encryption } from '../../utils/encryption';
import AlertAlgo from '../controls/AlertSnack';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/pro-duotone-svg-icons";
import stylesL from '../../styles/login.module.css';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        paddingLeft: '10px',
        paddingRight: '10px',
        paddingTop: '10px'
    },
    formControl: {
        marginTop: '10px'
    },
    saveButton: {
        width: '100%',
        '&:disabled': {
            backgroundColor: '#bfbfbf !important'
        }
    }
}));

function SavePIN(props) {
    const classes = useStyles();
    const [oldPIN, setOldPIN] = React.useState('');
    const [newPIN, setNewPIN] = React.useState('');
    const [confirmPIN, setConfirmPIN] = React.useState('');
    const [isSaved, setIsSaved] = React.useState(false);
    const [showOldPIN, setShowOldPIN] = React.useState(false);
    const [showPIN, setShowPIN] = React.useState(false);
    const [showPINConfirm, setShowPINConfirm] = React.useState(false);
    const alertRef = useRef(null);

    useEffect(() => {
        resize();
    }, []);

    const resize = () => {
        setTimeout(function () {
            const height = window.innerHeight;
            const titleBar = document.querySelector('#appBarTitle').clientHeight;

            const customHeight = height - titleBar - 50;

            const element = document.querySelector('#divPIN');

            if (element !== undefined && element !== null) {
                element.style.height = `${customHeight}px`;
                element.style.overflowX = 'hidden';
            }
        }, 1000);
    }

    const validatePIN = () => {
        return new Promise(async (resolve) => {
            let message = '';

            if (oldPIN.length === 0 || newPIN.length === 0 || confirmPIN.length === 0) {
                message = i18next.t('pin:validation.message.empty-fields');
            } else if (newPIN !== confirmPIN) {
                message = i18next.t('pin:validation.message.confirm-pin');
            }

            return resolve(message);
        });
    }

    const handleClose = (success) => {
        props.handleClose(success);
    }

    const handleSave = async () => {
        setIsSaved(true);

        let message = '';

        message = await validatePIN();

        if (message.length > 0) {
            showMessageError(message);
            setIsSaved(false);
        }
        else {
            const oldPINEncrypted = encryption(oldPIN);
            const newPINEncrypted = encryption(newPIN);

            savePIN(oldPINEncrypted, newPINEncrypted).then(result => {
                if (result != null && result.status == StatusCode.Ok) {
                    showMessageSuccess(i18next.t('pin:success.message.pin-saved'));
                    setIsSaved(false);

                    resetForm();

                    if (props.handleClose != undefined) {
                        setTimeout(() => {
                            handleClose();
                        }, 1500);
                    }
                    return;
                } else if (result.status === StatusCode.InvalidOldPIN) {
                    message = i18next.t('pin:validation.message.old_pin_does_not_match')
                } else if (result.status === StatusCode.PINLong) {
                    message = i18next.t('pin:validation.message.pin-too-long')
                } else {
                    // Error
                    message = i18next.t('error:generic-error')
                }
                showMessageError(message);
                setIsSaved(false);
            }).catch(error => {
                showMessageError(i18next.t('error:generic-error'));
                setIsSaved(false);
            });
        }
    };

    const resetForm = () => {
        setOldPIN('');
        setNewPIN('');
        setConfirmPIN('');
    }

    const onChangeOldPIN = (event) => {
        setOldPIN(event.target.value.trim());
    }

    const onChangeNewPIN = (event) => {
        setNewPIN(event.target.value.trim());
    }

    const onChangeConfirmPIN = (event) => {
        setConfirmPIN(event.target.value.trim());
    }

    const showMessageSuccess = (message) => {
        alertRef.current.showMessageSuccess(message);
    }

    const showMessageError = (message) => {
        alertRef.current.showMessageError(message);
    }

    const handleClickShowOldPIN = () => {
        setShowOldPIN(!showOldPIN);
    };

    const handleClickShowPIN = () => {
        setShowPIN(!showPIN);
    };

    const handleClickShowPINConfirm = () => {
        setShowPINConfirm(!showPINConfirm);
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    return (
        <div id="divPIN" className={classes.root}>
            <FormControl fullWidth className={classes.formControl}>
                <TextField
                    id="oldPIN"
                    label={i18next.t('pin:form.oldPIN')}
                    type={showOldPIN ? 'text' : 'password'}
                    value={oldPIN}
                    onChange={onChangeOldPIN}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowOldPIN}
                                    onMouseDown={handleMouseDownPassword}
                                    className={stylesL.eyeIcon}
                                >
                                    <FontAwesomeIcon icon={showOldPIN ? faEye : faEyeSlash} className={stylesL.icon_passwordVisibility} />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
            </FormControl>
            <FormControl fullWidth className={classes.formControl}>
                <TextField
                    id="newPIN"
                    label={i18next.t('pin:form.newPIN')}
                    type={showPIN ? 'text' : 'password'}
                    value={newPIN}
                    onChange={onChangeNewPIN}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPIN}
                                    onMouseDown={handleMouseDownPassword}
                                    className={stylesL.eyeIcon}
                                >
                                    <FontAwesomeIcon icon={showPIN ? faEye : faEyeSlash} className={stylesL.icon_passwordVisibility} />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
            </FormControl>
            <FormControl fullWidth className={classes.formControl}>
                <TextField
                    id="confirmPIN"
                    label={i18next.t('pin:form.confirmationPIN')}
                    type={showPINConfirm ? 'text' : 'password'}
                    value={confirmPIN}
                    onChange={onChangeConfirmPIN}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPINConfirm}
                                    onMouseDown={handleMouseDownPassword}
                                    className={stylesL.eyeIcon}
                                >
                                    <FontAwesomeIcon icon={showPINConfirm ? faEye : faEyeSlash} className={stylesL.icon_passwordVisibility} />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
            </FormControl>
            <div className="divButtons">
                <Button onClick={handleSave} loading={isSaved} variant="success" className={classes.saveButton}>
                    {i18next.t('buttons:save')}
                </Button>
            </div>
            <AlertAlgo ref={alertRef} />
        </div>
    );
}

export default withNamespaces(['pin', 'buttons', 'error'], { wait: true })(SavePIN);